import React, { useState, useEffect, useContext } from 'react';
import { Search, Filter, Eye, Edit, Trash2, X } from 'lucide-react';
import { 
  FaSearch, 
  FaFilter, 
  FaDownload, 
  FaEye, 
  FaPlus, 
  FaCheck, 
  FaTimes,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUniversity,
  FaGraduationCap,
  FaDollarSign,
  FaClock,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaExternalLinkAlt,
  FaBookmark,
  FaBookmark as FaBookmarkSolid,
  FaSync,
  FaInfoCircle,
  FaUserGraduate,
  FaUsers,
  FaUserTie,
  FaFileAlt,
  FaLink,
  FaCity,
  FaGlobe,
  FaEnvelope,
  FaMagic,
  FaStar,
  FaLock,
  FaEdit,
  FaTrash,
  FaMapPin,
  FaQuestionCircle
} from 'react-icons/fa';
import { saasApiAxios } from '../../lib/axios-interceptor';
import { encodeImageUrl } from '../../utils/helpers';
import { AuthContext } from '../../context/AuthContext';
import { canManageOpportunities } from '../../utils/permissions';
import PhdConnectHelpContent from './PhdConnectHelpContent';

// Modal for viewing opportunity details
function OpportunityDetailsModal({ opportunity, onClose, loading }) {
  if (!opportunity && !loading) return null;
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };
  
  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl border border-[#FFD6D4] w-[90%] max-w-4xl max-h-[90vh] flex flex-col relative overflow-hidden ml-20">
        {/* Header with close button */}
        <div className="flex items-center justify-between p-6 border-b border-[#FFD6D4] bg-gradient-to-r from-[#FFEEED] to-white">
          <h1 className="text-2xl font-bold text-[#DB4F40]">Opportunity Details</h1>
          <button
            className="flex items-center justify-center w-10 h-10 rounded-full bg-[#DB4F40] text-white hover:bg-[#C64537] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            onClick={onClose}
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content area with scroll */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#FFD6D4] border-t-[#DB4F40]"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* University and location section */}
              <div className="flex flex-col lg:flex-row gap-6 items-start">
                <div className="flex-shrink-0 bg-white p-4 rounded-2xl shadow-lg border-2 border-[#FFD6D4] flex items-center justify-center min-w-[120px]">
                  <img
                    src={`${process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : ''}/university/${encodeImageUrl(opportunity.university)}.png`}
                    alt={opportunity.university}
                    className="h-24 w-24 object-contain"
                    onError={e => (e.target.style.display = 'none')}
                  />
                  {!opportunity.university && (
                    <FaUniversity className="h-24 w-24 text-[#DB4F40]/30 p-4" />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-[#1B222A] mb-3">{opportunity.university}</h2>
                  <div className="flex items-center text-[#1B222A]/80 space-x-3 mb-2">
                    <FaMapPin className="text-[#DB4F40] flex-shrink-0 text-lg" />
                    <span className="text-xl font-medium">{opportunity.city}, {opportunity.country}</span>
                  </div>
                  <div className="text-lg text-[#1B222A]/70 font-medium">{opportunity.department}</div>
                </div>
              </div>

              {/* Title */}
              <div className="bg-gradient-to-r from-[#FFEEED] to-white p-6 rounded-2xl border border-[#FFD6D4]">
                <h1 className="text-3xl font-bold text-[#DB4F40] leading-tight">{opportunity.title}</h1>
              </div>

              {/* Key details grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {opportunity.supervisor && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4] hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-3">
                      <FaUserTie className="text-[#DB4F40] mr-2 text-lg" />
                      <span className="font-bold text-[#1B222A] text-lg">Supervisor</span>
                    </div>
                    <span className="text-[#1B222A]/80 text-base">{opportunity.supervisor}</span>
                  </div>
                )}
                {opportunity.closing_date && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4] hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-3">
                      <FaCalendarAlt className="text-[#DB4F40] mr-2 text-lg" />
                      <span className="font-bold text-[#1B222A] text-lg">Deadline</span>
                    </div>
                    <span className="text-[#1B222A]/80 text-base">{formatDate(opportunity.closing_date)}</span>
                  </div>
                )}
                {opportunity.fully_funded !== undefined && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4] hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-3">
                      <FaDollarSign className="text-[#DB4F40] mr-2 text-lg" />
                      <span className="font-bold text-[#1B222A] text-lg">Funding</span>
                    </div>
                    <span className="text-[#1B222A]/80 text-base">
                      {opportunity.fully_funded ? 'Fully Funded' : 'Partial/Not Funded'}
                    </span>
                  </div>
                )}
              </div>

              {/* Description */}
              {opportunity.description && (
                <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4]">
                  <h3 className="text-xl font-bold text-[#DB4F40] mb-4 flex items-center">
                    <FaFileAlt className="mr-2" />
                    Description
                  </h3>
                  <div className="prose prose-lg max-w-none text-[#1B222A]/90 leading-relaxed">
                    <div dangerouslySetInnerHTML={{ __html: opportunity.description }} />
                  </div>
                </div>
              )}

              {/* AI Summary */}
              {opportunity.ai_summary && (
                <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4]">
                  <h3 className="text-xl font-bold text-[#DB4F40] mb-4 flex items-center">
                    <FaMagic className="mr-2" />
                    AI Summary
                  </h3>
                  <div className="prose prose-lg max-w-none text-[#1B222A]/90 leading-relaxed">
                    <p>{opportunity.ai_summary}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const SaasPhdConnectSection = () => {
  const { currentUser } = useContext(AuthContext);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 30,
    total: 0
  });

  // Filter state
  const [filters, setFilters] = useState({
    title: '',
    department: '',
    supervisor: '',
    country: '',
    university: '',
    funded: ''
  });

  const [pendingFilters, setPendingFilters] = useState({
    title: '',
    department: '',
    supervisor: '',
    country: '',
    university: '',
    funded: ''
  });

  // Fetch opportunities for current university
  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const params = {
        limit: pagination.limit,
        offset: (pagination.page - 1) * pagination.limit,
        ...(filters.title && { title: filters.title }),
        ...(filters.department && { department: filters.department }),
        ...(filters.supervisor && { supervisor: filters.supervisor }),
        ...(filters.country && { country: filters.country }),
        ...(filters.university && { university: filters.university }),
        ...(filters.funded === 'true' && { funded: 'true' })
      };

      const response = await saasApiAxios.get('/api/phd-connect/opportunities', { params });
      setOpportunities(response.data.data || []);
      setPagination(prev => ({ ...prev, total: response.data.pagination?.total || 0 }));
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get paginated opportunities
  const paginatedOpportunities = opportunities;

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setPendingFilters(prev => ({ ...prev, [name]: value }));
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };

  // Get funding text
  const getFundingText = (fullyFunded) => {
    return fullyFunded ? 'Fully Funded' : 'Partial/Not Funded';
  };

  // Handle view opportunity
  const handleViewOpportunity = async (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowDetailsModal(true);
  };

  // Handle edit opportunity (redirect to edit page)
  const handleEditOpportunity = async (opportunity) => {
    window.open(`/saas/edit-opportunity/${opportunity.id}`, '_blank');
  };

  // Handle delete opportunity
  const handleDeleteOpportunity = async (opportunity) => {
    if (window.confirm(`Are you sure you want to delete "${opportunity.title}"?`)) {
      try {
        await saasApiAxios.delete(`/api/saas/opportunities/${opportunity.id}`);
        fetchOpportunities(); // Refresh the list
        alert('Opportunity deleted successfully!');
      } catch (error) {
        console.error('Error deleting opportunity:', error);
        alert('Failed to delete opportunity. Please try again.');
      }
    }
  };

  useEffect(() => {
    fetchOpportunities();
  }, [pagination.page, filters]);

  if (loading && opportunities.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DB4F40]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFEEED] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto mb-8">
        <h1 className="text-3xl font-extrabold text-[#DB4F40] mb-6">PhD Connect Help Center</h1>
        <p className="text-lg text-[#1B222A]/70">Everything you need to know about embedding PhD opportunities on your website</p>
      </div>
      
      <div className="relative max-w-6xl mx-auto">
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-[#FFD6D4] overflow-hidden">
          <div className="p-8 sm:p-12">
            <PhdConnectHelpContent />
          </div>
        </div>
      </div>
      
      {/* Opportunity Details Modal */}
      {showDetailsModal && (
        <OpportunityDetailsModal
          opportunity={selectedOpportunity}
          loading={detailsLoading}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
    </div>
  );
};

export default SaasPhdConnectSection; 